import {
  type InfiniteData,
  type FetchNextPageOptions,
  useInfiniteQuery,
  useQueries,
  type QueryKey,
  type UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { type AxiosResponse } from "axios";
import toNumber from "lodash/toNumber";
import uniq from "lodash/uniq";
import { useEffect, useRef } from "react";

import { getNextLink, getPrevLink } from "@/api";
import { useApi, useLoggedIn } from "@/hooks";
import {
  groupRelationshipSchema,
  relationshipSchema,
  type GroupRelationship,
  type Relationship,
} from "@/schemas";
import { filteredArray } from "@/schemas/utils";
import { flattenPages } from "@/utils/queries";

import { queryClient } from "../client";
import { QueryKeys } from "../query-keys";
import { getEntityKey } from "../utils";

import type { Entity, EntitySchema, Headers, Params } from "../types";

type Relationships = (Relationship | GroupRelationship)[];

const getRelationshipConfig = (category: "account" | "group") => {
  if (category === "group") {
    return {
      queryKey: (ids: string[]) => QueryKeys.groupRelationships.list(ids),
      globalQueryKey: QueryKeys.groupRelationships.global,
      endpoint: "/api/v1/groups/relationships",
      schema: groupRelationshipSchema,
    };
  }

  return {
    queryKey: (ids: string[]) => QueryKeys.relationships.list(ids),
    globalQueryKey: QueryKeys.relationships.global,
    endpoint: "/api/v1/accounts/relationships",
    schema: relationshipSchema,
  };
};

export interface UseEntitiesOptions<TEntity extends Entity> extends Pick<
  UseInfiniteQueryOptions,
  | "enabled"
  | "keepPreviousData"
  | "staleTime"
  | "cacheTime"
  | "refetchOnMount"
  | "refetchInterval"
> {
  dir?: "next" | "prev";
  endpoint: string;
  formatResult?(result: TEntity[], response: AxiosResponse): TEntity[];
  getTotalCount?(totalCount: number): void;
  getAdZone?(adZone: number): void;
  getResponseHeaders?(headers: AxiosResponse["headers"]): void;
  headers?: Headers;
  onSuccess?(data: TEntity[]): void;
  paginationType?: "offset" | "link";
  params?: Params;
  queryKey: QueryKey;
  responseKey?: string;
  schema: EntitySchema<TEntity>;
  // Config for handling relationships, if not provided, relationships will not be fetched
  relationshipConfig?: {
    category: "account" | "group";
    // Function to extract IDs from the result, if not provided, it will use the default mapping
    extractIds?: (result: TEntity[]) => string[];
    // If true, relationships will be merged with the result, for custom merging, provide a function
    mergeRelationships?:
      | true
      | ((result: TEntity[], relationships: Relationships) => TEntity[]);
  };
}

export function useEntities<TEntity extends Entity>(
  options: UseEntitiesOptions<TEntity>,
) {
  const {
    dir = "next",
    endpoint,
    formatResult,
    getTotalCount,
    getResponseHeaders,
    getAdZone,
    onSuccess,
    paginationType = "link",
    params = {},
    responseKey,
    schema,
    relationshipConfig,
    ...filteredOptions
  } = options;
  const { isLoggedIn } = useLoggedIn();
  const api = useApi();

  const entity = getEntityKey(filteredOptions.queryKey);

  const fetchRelationships = async (result: TEntity[]) => {
    const { extractIds, category } = relationshipConfig!;
    const relationship = getRelationshipConfig(category);

    const ids = extractIds?.(result) || result.map((entity) => entity.id);
    if (ids.length === 0 || !isLoggedIn || !relationship) return [];
    const sortedIds = uniq(ids.sort());
    const relationshipsEntity = getEntityKey(relationship.queryKey([]));

    const { data } = await queryClient.fetchQuery({
      queryKey: relationship.queryKey(sortedIds),
      meta: { entity: relationshipsEntity },
      queryFn: async () => {
        const response = await api.get(relationship.endpoint, {
          params: { id: sortedIds },
        });
        return response.data;
      },
      staleTime: 300000, // 5 minutes
    });

    const formattedData = filteredArray(relationship.schema).parse(data);

    return formattedData as Relationships;
  };

  const mergeRelationships = (
    result: TEntity[],
    relationships: Relationships | undefined,
  ) => {
    if (!relationshipConfig || !relationships) return result;

    const mergeFn = relationshipConfig.mergeRelationships;
    let mergedResult = [...result];

    if (mergeFn) {
      mergedResult =
        typeof mergeFn === "function"
          ? mergeFn(mergedResult, relationships)
          : mergedResult.map((entity) => ({
              ...entity,
              relationship: relationships.find(
                (relationship) => relationship.id === entity.id,
              ),
            }));
    }

    return mergedResult;
  };

  const getIdsPerPage = (result: InfiniteData<TEntity[]> | undefined) => {
    const ids: string[][] = [];
    const extractIdsFn = relationshipConfig?.extractIds;

    if (!result || !result.pages) return ids;

    (result.pages || result).forEach((page: any) => {
      const pageIds =
        extractIdsFn?.(page.result) ||
        page.result.map((entity: TEntity) => entity.id);
      const sortedIds = uniq(pageIds.sort()) as string[];
      ids.push(sortedIds);
    });

    return ids;
  };

  const queryInfo = useInfiniteQuery<unknown, unknown, TEntity[]>({
    ...filteredOptions,
    meta: { entity },
    queryFn: async ({ pageParam, signal }) => {
      const nextPageLink = pageParam?.link;
      const offset = pageParam?.offset;
      const uri = nextPageLink || endpoint;
      const response = await api.get<TEntity>(uri, {
        params: { ...params, offset },
        signal,
      });
      const link =
        dir === "next" ? getNextLink(response) : getPrevLink(response);
      let hasMore = !!link;
      const data = responseKey
        ? (response.data as any)[responseKey]
        : response.data;
      let result = filteredArray(schema).parse(data);

      const pageSize = toNumber(params.limit) || 20;
      if (paginationType === "offset") {
        hasMore = data.length >= pageSize;
      }

      if (formatResult) {
        result = formatResult(result, response);
      }

      if (getTotalCount) {
        getTotalCount(toNumber(response.headers["x-total-count"]));
      }

      if (getAdZone) {
        const zone = response.headers["x-ad-zone"];

        if (zone) {
          getAdZone(toNumber(zone));
        }
      }

      if (getResponseHeaders) {
        getResponseHeaders(response.headers);
      }

      if (relationshipConfig) {
        const relationships = await fetchRelationships(result);
        result = mergeRelationships(result, relationships);
      }

      return {
        result,
        link,
        hasMore,
        nextOffset: (offset ?? 0) + data.length,
      };
    },
    getNextPageParam: (config: any) => {
      if (!config.hasMore) {
        return undefined;
      }

      if (paginationType === "offset") {
        return { offset: config.nextOffset };
      }

      return { link: config.link };
    },
  });

  const ids = getIdsPerPage(queryInfo.data);
  const relationships = useGetRelationships(ids, relationshipConfig?.category);
  let entities = (flattenPages(queryInfo.data) as TEntity[] | undefined) || [];

  if (entities.length > 0 && relationshipConfig?.mergeRelationships) {
    entities = mergeRelationships(entities, relationships);
  }

  function loadNextPage(options?: FetchNextPageOptions) {
    const { fetchNextPage, isFetchingNextPage, hasNextPage } = queryInfo;

    if (hasNextPage && !isFetchingNextPage) {
      return fetchNextPage({ ...options, cancelRefetch: false });
    }
  }

  function loadPrevPage(options?: FetchNextPageOptions) {
    const { fetchPreviousPage, hasPreviousPage, isFetchingPreviousPage } =
      queryInfo;

    if (hasPreviousPage && !isFetchingPreviousPage) {
      return fetchPreviousPage({ ...options, cancelRefetch: false });
    }
  }

  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  useEffect(() => {
    if (queryInfo.isSuccess && queryInfo.data) {
      onSuccessRef.current?.(flattenPages(queryInfo.data) as any);
    }
  }, [queryInfo.isSuccess, queryInfo.data]);

  return {
    ...queryInfo,
    entities,
    fetchNextPage: loadNextPage,
    fetchPreviousPage: loadPrevPage,
  };
}

const useGetRelationships = (
  ids: string[][],
  category: "account" | "group" | undefined,
) => {
  const api = useApi();
  const { isLoggedIn } = useLoggedIn();
  const relationship = getRelationshipConfig(category || "account");
  const relationshipEntity = getEntityKey(relationship.queryKey([]));

  const result = useQueries<Relationships>({
    queries: ids.map((sortedIds) => ({
      queryKey: relationship.queryKey(sortedIds),
      meta: { entity: relationshipEntity },
      queryFn: async () => {
        const response = await api.get(relationship.endpoint, {
          params: { id: sortedIds },
        });
        return response.data;
      },
      enabled: isLoggedIn && sortedIds.length > 0 && !!category,
    })),
  });

  const relationships = result
    .map((r) => r.data)
    .flat()
    .filter(Boolean) as Relationships;

  return relationships;
};
